import {
  useCallback,
  useRef,
  type ReactElement,
  type SyntheticEvent,
} from "react";

import { useLabels } from "~/hooks/useLabels";
import { Button } from "~/components/button/Button";
import { Text } from "~/components/text/Text";
import { useScrollWithShadow } from "~/hooks/useScrollWithShadow";

import "./SidePanelLayout.css";

export interface SidePanelLayoutProps {
  title: string | null;
  onRequestClose?: () => void;
  children?: ReactElement;
}

export function SidePanelLayout({
  title,
  onRequestClose,
  children,
}: SidePanelLayoutProps) {
  const { t } = useLabels();

  const bodyRef = useRef<HTMLDivElement>(null);

  const [scrollContainerRef, scrollWithShadow] =
    useScrollWithShadow<HTMLDivElement>();

  const handleScroll = useCallback(
    (event: SyntheticEvent) => {
      scrollWithShadow.onScroll(event);
    },
    [scrollWithShadow],
  );

  const handleCloseClick = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  return (
    <div className="SidePanelLayout">
      <div className="SidePanelLayout__header">
        {title && (
          <Text size="lg" weight="bold">
            {title}
          </Text>
        )}
        <Button
          appearance="control"
          icon="xmark"
          label={t("common.cta.close")}
          shape="circle"
          size="sm"
          onClick={handleCloseClick}
        />
      </div>
      <div className="SidePanelLayout__body" ref={bodyRef}>
        <div
          ref={scrollContainerRef}
          className="SidePanelLayout__scroll-outer"
          onTouchMove={handleScroll}
          onWheel={handleScroll}
          // because this is scrollable, keyboard users need a way to tab to it
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={scrollWithShadow.boxShadow ? 0 : undefined}
        >
          <div className="SidePanelLayout__scroll-inner">{children}</div>
        </div>
        <div
          className="SidePanelLayout__scroll-shadows"
          style={{ boxShadow: scrollWithShadow.boxShadow }}
        />
      </div>
    </div>
  );
}
